import React, { useState } from 'react';
import { FaRoute } from "react-icons/fa";
import { useNavigate, useLocation } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { useAuth } from '../../../context/AuthContext';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode, Navigation, Keyboard, Mousewheel } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/navigation';

import { Train, Bus, ChevronDown, ChevronUp, Info, ExternalLink, Share2 } from 'lucide-react';
import CabOptions, { defaultCabCategory, selectedCabPrice } from './CabOptions';
import { fetchFareQuote, FallbackAvailableError } from '../../../services/flightBookingAPI';
import LoadingModal from '../../modal/LoadingModal';

import './ResultCard.css';

const compareClasses = (c1, c2) => {
    if (!c1 || !c2) return false;
    const normalize = (s) => String(s).replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    return normalize(c1) === normalize(c2);
};

const FlightIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M2 20H18" stroke="#6366F1" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" />
        <path d="M3.82496 12.1661C3.54996 11.9661 3.29996 11.7161 2.99997 10.8411C2.9186 10.6241 2.61108 9.53619 2.34997 8.54109C2.12972 7.7017 1.93346 6.93555 2.02497 6.74109C2.09998 6.54109 2.19996 6.39109 2.52496 6.19109C2.72496 6.06802 3.74996 5.81609 3.94996 5.76609C4.14996 5.71609 4.42495 5.69109 4.64996 5.76609C5.07496 5.84109 5.94996 7.11609 6.17496 7.26609C6.27495 7.36609 6.59996 7.657 6.97496 7.69109C7.24996 7.71609 7.52497 7.64109 7.82498 7.51609C8.09997 7.40151 13.525 4.76609 14.025 4.54109C18.1 2.84109 21.06 5.63609 21.51 6.23609C21.975 6.81609 22.075 6.99109 21.95 7.49109C21.7884 8.01609 21.35 8.11609 21.1 8.19109C20.85 8.26609 17.4 9.19109 16.05 9.56609C15.7551 9.6621 15.6111 9.85492 15.575 9.89109C15.4 10.1411 14.605 11.8411 14.38 12.2161C14.225 12.6161 13.8 13.1161 13.25 13.3161C12.675 13.5161 11.675 13.7411 11.45 13.8161C11.225 13.8911 10.7 14.0411 10.525 13.9911C10.3 13.9411 10.085 13.7161 10.185 13.3661C10.285 13.0161 10.475 12.0411 10.5 11.8911C10.525 11.7411 10.775 11.1161 10.5 11.0911C10.45 11.0161 9.92497 11.2411 9.14997 11.4161C8.57419 11.5782 7.97119 11.7386 7.54996 11.8411C5.92496 12.3161 5.0449 12.4411 4.84996 12.4411C4.47496 12.4411 4.19996 12.3911 3.82496 12.1661Z" stroke="#6366F1" stroke-width="2.2" />
    </svg>

);
const TrainIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M7 6.99805V3.09766C7 2.54537 7.44772 2.09766 8 2.09766C8.55228 2.09766 9 2.54537 9 3.09766V6.99805L9.01465 7.29395C9.08279 7.9808 9.38657 8.6268 9.87891 9.11914C10.4415 9.68163 11.2044 9.99805 12 9.99805L12.2969 9.9834C12.9834 9.91516 13.6289 9.61123 14.1211 9.11914C14.6134 8.6268 14.9172 7.9808 14.9854 7.29395L15 6.99805V3.09766C15 2.54537 15.4477 2.09766 16 2.09766C16.5523 2.09766 17 2.54537 17 3.09766V6.99805L16.9941 7.24512C16.9329 8.48111 16.4143 9.65401 15.5352 10.5332C14.6561 11.4121 13.4837 11.9299 12.248 11.9912L12 11.998C10.674 11.998 9.40251 11.4708 8.46484 10.5332C7.58565 9.65401 7.06706 8.48111 7.00586 7.24512L7 6.99805Z" fill="#FF670A" stroke="#FF670A" stroke-width="0.5" />
        <path d="M7.29296 13.2927C7.65908 12.9266 8.23808 12.904 8.63085 13.2244L8.70703 13.2927L9.70703 14.2927L9.77539 14.3689C10.0957 14.7617 10.0731 15.3407 9.70703 15.7068C9.34091 16.0729 8.76191 16.0955 8.36914 15.7752L8.29296 15.7068L7.29296 14.7068L7.2246 14.6306C6.90425 14.2379 6.92685 13.6589 7.29296 13.2927Z" fill="#FF670A" stroke="#FF670A" stroke-width="0.5" />
        <path d="M15.3691 13.2244C15.7619 12.904 16.3409 12.9266 16.707 13.2927C17.0731 13.6589 17.0957 14.2379 16.7754 14.6306L16.707 14.7068L15.707 15.7068C15.3165 16.0973 14.6835 16.0973 14.293 15.7068C13.9024 15.3163 13.9024 14.6833 14.293 14.2927L15.293 13.2927L15.3691 13.2244Z" fill="#FF670A" stroke="#FF670A" stroke-width="0.5" />
        <path d="M18.9912 9.65332C18.9055 7.92303 18.181 6.28061 16.9502 5.0498C15.7194 3.819 14.077 3.09452 12.3467 3.00879L12 3C10.1435 3 8.36256 3.73705 7.0498 5.0498C5.73705 6.36256 5 8.14348 5 10V14C5 16.2477 6.75228 18 9 18H15C17.2477 18 19 16.2477 19 14V10L18.9912 9.65332ZM21 14C21 17.3523 18.3523 20 15 20H9C5.64772 20 3 17.3523 3 14V10C3 7.61305 3.94791 5.32357 5.63574 3.63574C7.32357 1.94791 9.61305 1 12 1C14.3869 1 16.6764 1.94791 18.3643 3.63574C20.0521 5.32357 21 7.61305 21 10V14Z" fill="#FF670A" stroke="#FF670A" stroke-width="0.5" />
        <path d="M7.16797 18.4454C7.47432 17.9859 8.09516 17.8617 8.55469 18.1681C9.01421 18.4744 9.13838 19.0952 8.83203 19.5548L6.83203 22.5548C6.52568 23.0143 5.90484 23.1385 5.44531 22.8321C4.98578 22.5258 4.86161 21.9049 5.16797 21.4454L7.16797 18.4454Z" fill="#FF670A" stroke="#FF670A" stroke-width="0.5" />
        <path d="M15.4456 18.1681C15.8765 17.8808 16.4495 17.972 16.7718 18.3634L16.8323 18.4455L18.8323 21.4455L18.8851 21.5333C19.1221 21.9813 18.9858 22.545 18.555 22.8322C18.124 23.1195 17.5511 23.0283 17.2288 22.6369L17.1683 22.5548L15.1683 19.5548L15.1155 19.4669C14.8785 19.019 15.0148 18.4553 15.4456 18.1681Z" fill="#FF670A" stroke="#FF670A" stroke-width="0.5" />
    </svg>
);





const firstWord = (value) => {
    const text = String(value || '').trim();
    return text ? text.split(/\s+/)[0] : '';
};

const toTitleCase = (str) => str ? str.replace(/\b\w/g, c => c.toUpperCase()) : str;
const ResultCard = ({ card, onViewMap, onShare, onBookClick, onShowError }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [refreshingIdx, setRefreshingIdx] = useState(null);
    const [refreshedInfo, setRefreshedInfo] = useState({});
    const [isBookingLoading, setIsBookingLoading] = useState(false);
    const [selectedSeatClass, setSelectedSeatClass] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();
    const { openLoginModal } = useAuth();

    const mode = card?.type || card?.segment?.mode;
    const isFlight = mode === 'flight';
    const isBus = mode === 'bus';

    // Derive bus seatStatus from flat seat_avail_ui if parent didn't compute it.
    // New flat format: { avl: "28", is_avail: true, ... }
    // Old nested format (no longer produced): { General: { available: 28, ... } }
    const busSeatStatus = (() => {
        if (!isBus) return card.seatStatus;
        if (card.seatStatus) return card.seatStatus;
        const seatUI = card?.segment?.seat_avail_ui;
        if (!seatUI) return null;
        // flat format
        if (seatUI.avl !== undefined) {
            const avl = seatUI.avl;
            const count = parseInt(avl, 10);
            return isNaN(count) ? (seatUI.is_avail ? 'Available' : 'Sold out') : `${count} Seats Available`;
        }
        // legacy nested format fallback
        const generalEntry = seatUI['General'];
        if (generalEntry) {
            const count = generalEntry.available;
            return typeof count === 'number' ? `${count} Seats Available` : 'Available';
        }
        return null;
    })();

    // Use busSeatStatus for bus, original card.seatStatus for everything else
    const effectiveSeatStatus = isBus ? busSeatStatus : card.seatStatus;

    // Bus promo comes from the BUS segment object itself.
    // The backend uses flat_off_flight_promo_desc for this field even on bus results.
    const busPromoDesc = isBus
        ? (card?.flat_off_bus_promo_desc || card?.segment?.flat_off_bus_promo_desc ||
           card?.segment?.flat_off_flight_promo_desc || card?.flat_off_flight_promo_desc || null)
        : null;

    const handleBookFlight = async () => {
        // Get Firebase user token
        const auth = getAuth();
        const user = auth.currentUser;

        // Save source and query to localStorage for persistence across reloads/logouts
        localStorage.setItem("booking_source", "search");
        localStorage.setItem("booking_search_query", location.search);

        const segment = card?.segment;
        const trace_id = segment?.trace_id;
        const result_idx = segment?.result_idx;
        const is_lcc = segment?.is_lcc;
        const valid_till_ts = segment?.valid_till_ts;

        setIsBookingLoading(true);

        try {
            let userToken = null;
            if (user) {
                userToken = await user.getIdToken();
            }

            // Call verify API first
            const fareQuoteResponse = await fetchFareQuote(trace_id, result_idx, userToken);

            if (onBookClick) onBookClick();

            // Build URL with query parameters
            const params = new URLSearchParams();
            if (trace_id) params.append("trace_id", trace_id);
            if (result_idx !== undefined && result_idx !== null)
                params.append("result_idx", result_idx);
            if (is_lcc !== undefined && is_lcc !== null)
                params.append("is_lcc", is_lcc);
            if (valid_till_ts !== undefined && valid_till_ts !== null)
                params.append("valid_till_ts", valid_till_ts);

            const url = `/book-flight?${params.toString()}`;

            // Navigate with both URL params and location state
            navigate(url, {
                state: {
                    preFetchedQuote: fareQuoteResponse,
                    trace_id: trace_id,
                    result_idx: result_idx,
                    is_lcc: is_lcc,
                    valid_till_ts: valid_till_ts,
                    baggage: segment?.baggage,
                    userToken: userToken,
                    source: "search",
                    searchQuery: location.search,
                    flightDetails: {
                        departure: {
                            location: segment?.origin_code,
                            time: segment?.origin_dep_dt ? new Date(segment.origin_dep_dt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '',
                            date: segment?.origin_dep_dt ? new Date(segment.origin_dep_dt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : ''
                        },
                        arrival: {
                            location: segment?.dest_code,
                            time: segment?.dest_arr_dt ? new Date(segment.dest_arr_dt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '',
                            date: segment?.dest_arr_dt ? new Date(segment.dest_arr_dt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : ''
                        },
                        duration: card.duration,
                        stops: card.stops,
                        price: card.price,
                        currency: card.currency || '₹',
                        tag: card.tags?.[0] || '',
                        modes: [mode],
                    },
                },
            });
        } catch (error) {
            console.error("Booking failed:", error);

            // Handle unauthorized error - show login modal
            const isAuthError =
                error.status === 401 ||
                error.response?.status === 401 ||
                error.message?.toLowerCase().includes("unauthorized") ||
                error.message?.toLowerCase().includes("login required") ||
                error.message?.toLowerCase().includes("auth verifier");

            if (isAuthError) {
                openLoginModal(() => handleBookFlight());
            } else if (error instanceof FallbackAvailableError) {
                if (onShowError) onShowError(error);
            } else {
                if (onShowError) onShowError({
                    message: error.message || "Something went wrong. Please try again.",
                    operationName: "Booking Error"
                });
            }
        } finally {
            setIsBookingLoading(false);
        }
    };

    const handleRefresh = async (segment, cls, idx) => {
        setRefreshingIdx(idx);
        try {
            // Robust date extraction helper
            const parseDate = (dStr) => {
                if (!dStr) return null;
                // If it's a number (timestamp)
                if (!isNaN(dStr) && typeof dStr === 'number') {
                    return new Date(dStr);
                }
                // If it looks like a number in a string
                if (typeof dStr === 'string' && /^\d+$/.test(dStr)) {
                    return new Date(Number(dStr));
                }

                // Handle DD-MM-YYYY or DD/MM/YYYY
                const parts = String(dStr).split(/[-/\s]/);
                if (parts.length >= 3) {
                    const p0 = parseInt(parts[0], 10);
                    const p1 = parseInt(parts[1], 10);
                    const p2 = parseInt(parts[2], 10);

                    // Case DD-MM-YYYY (p2 is year)
                    if (p2 > 2000 && p1 >= 1 && p1 <= 12 && p0 >= 1 && p0 <= 31) {
                        return new Date(p2, p1 - 1, p0);
                    }
                    // Case YYYY-MM-DD (p0 is year)
                    if (p0 > 2000 && p1 >= 1 && p1 <= 12 && p2 >= 1 && p2 <= 31) {
                        return new Date(p0, p1 - 1, p2);
                    }
                }

                // Fallback to standard parser and check year
                const d = new Date(dStr);
                if (!isNaN(d.getTime())) {
                    if (d.getFullYear() > 2000) return d;
                }
                return null;
            };

            let dateObj = parseDate(segment.origin_dep_dt) ||
                parseDate(segment.origin?.dep_dt) ||
                parseDate(segment.departure_date) ||
                parseDate(segment.origin?.departure_date);

            const formattedDate = dateObj
                ? `${String(dateObj.getDate()).padStart(2, '0')}-${String(dateObj.getMonth() + 1).padStart(2, '0')}-${dateObj.getFullYear()}`
                : '';

            if (!formattedDate) {
                console.error('Refresh aborted: No valid date found in segment', segment);
                setRefreshingIdx(null);
                return;
            }

            const params = new URLSearchParams({
                train_num: segment.flight_num || segment.number || '',
                src: segment.origin_code || segment.origin?.code || '',
                dest: segment.dest_code || segment.destination?.code || '',
                date: formattedDate,
                pref_train_class: cls,
                quota: (segment.category && segment.category !== 'Reserved' && segment.category !== '(Reserved)') ? segment.category : 'GN',
                api_key: 'web_dev_1',
                get_avail_only: '1'
            }).toString();

            const url = `https://hh183pxn0f.execute-api.ap-south-1.amazonaws.com/development/search?${params}`;
            const res = await fetch(url, { method: "GET" });
            const data = await res.json();

            if (data.status === 200 && data.data?.seat_avail_ui) {
                setRefreshedInfo(prev => ({
                    ...prev,
                    [idx]: {
                        avl: data.data.seat_avail_ui.avl,
                        fare: data.data.seat_avail_ui.fare,
                        is_avail: data.data.seat_avail_ui.is_avail,
                        quota: data.data.seat_avail_ui.quota
                    }
                }));
            }
        } catch (e) {
            console.error('Refresh error:', e);
        } finally {
            setTimeout(() => setRefreshingIdx(null), 500);
        }
    };

    const formatPrice = (amount) => {
        if (amount === null || amount === undefined) return null;
        const val = parseFloat(typeof amount === 'string' ? amount.replace(/,/g, '') : amount);
        if (isNaN(val) || !amount) return null;
        return val.toLocaleString('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        });
    };

    // Cab selections (origin/dest pickup-drop cabs from TaxiBazaar)
    const [cabSel, setCabSel] = useState({
        origin: defaultCabCategory(card.originCab),
        dest: defaultCabCategory(card.destCab)
    });
    const cabTotal =
        selectedCabPrice(card.originCab, cabSel.origin) +
        selectedCabPrice(card.destCab, cabSel.dest);

    const displayPrice = (() => {
        // Show transport fare only — cab is an optional add-on shown in expanded section
        const refreshedEntries = Object.keys(refreshedInfo);
        if (refreshedEntries.length > 0) {
            const lastUpdatedKey = refreshedEntries[refreshedEntries.length - 1];
            const lastUpdated = refreshedInfo[lastUpdatedKey];
            if (lastUpdated && lastUpdated.fare) return formatPrice(lastUpdated.fare);
        }
        if (card.price) return card.price;
        return '-';
    })();

    return (
        <div className={`result-card-container ${isExpanded ? 'expanded' : ''}`} onClick={() => setIsExpanded(!isExpanded)}>
            {/* Card Header */}
            <div className="result-card-header">
                <div className="result-card-train-info">
                    <div className="result-card-train-icon-box">
                        {isFlight ? <FlightIcon /> : isBus ? <Bus size={24} color="#16A34A" strokeWidth={2.5} /> : <TrainIcon />}
                    </div>
                    {isBus ? (
                        <div className="result-card-bus-title">
                            <span className="result-card-train-name">
                                {card?.segment?.flight_name || card?.segment?.travel_name || ''}
                                {card?.segment?.seat_class_ui && (
                                    <span className="result-card-bus-type">
                                        {', '}{card.segment.seat_class_ui}
                                    </span>
                                )}
                            </span>
                        </div>
                    ) : (
                        <span className="result-card-train-name">
                            {[card?.segment?.flight_num, card?.segment?.flight_name].filter(s => s && s.trim()).join(', ')}
                        </span>
                    )}
                </div>
                <div className="result-card-tags">
                    <button
                        className="result-card-map-btn icon-only"
                        onClick={(e) => {
                            e.stopPropagation();
                            onViewMap();
                        }}
                        title="View Route on Map"
                    >
                        <FaRoute size={16} />
                    </button>
                    <button
                        className="result-card-map-btn share-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            onShare();
                        }}
                        title="Share Route"
                        style={{ width: 'auto', padding: '0 8px', gap: '4px' }}
                    >
                        <Share2 size={14} />
                        <span>Share</span>
                    </button>
                    {card.tags && card.tags.length > 0 && card.tags.map((tag, idx) => (
                        <span key={idx} className={`result-card-tag ${tag.toLowerCase()}`}>
                            {tag}
                        </span>
                    ))}
                </div>
            </div>

            {/* Card Content */}
            <div className="result-card-content">
                <div className="result-card-journey-row">
                    {/* Origin */}
                    <div className="result-card-station-box">
                        <span className="result-card-station-name">
                            {card?.segment?.origin_name || card?.segment?.origin?.origin_name || 'Origin'}
                        </span>
                        <span className="result-card-time-iata">
                            {isBus ? firstWord(card?.segment?.origin_name || card?.segment?.origin?.origin_name) : (card?.segment?.origin_code || card?.segment?.origin?.origin_code_iata || 'ORG')}, {
                                card?.segment?.origin_dep_dt
                                    ? new Date(card.segment.origin_dep_dt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                    : card?.segment?.origin?.origin_dep_dt || '--:--'
                            }
                        </span>
                        <span className="result-card-date">
                            {card?.segment?.origin_dep_dt
                                ? new Date(card.segment.origin_dep_dt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
                                : card?.segment?.origin?.departure_date || 'Date'}
                        </span>
                    </div>

                    {/* Path Visual */}
                    <div className="result-card-journey-visual">
                        <div className="result-card-line-dashed"></div>
                        <div className="result-card-duration-info">
                            <span className="result-card-duration">{card.duration || 'N/A'}</span>
                            <span className="result-card-halts">
                                {(card.stops || 0) === 0
                                    ? '0 change stop'
                                    : `${card.stops} change ${card.stops === 1 ? 'stop' : 'stops'}`
                                }
                            </span>

                        </div>
                    </div>

                    {/* Destination */}
                    <div className="result-card-station-box right">
                        <span className="result-card-station-name">
                            {card?.segment?.dest_name || card?.segment?.dest?.dest_name || 'Destination'}
                        </span>
                        <span className="result-card-time-iata">
                            {isBus ? firstWord(card?.segment?.dest_name || card?.segment?.dest?.dest_name) : (card?.segment?.dest_code || 'DST')}, {
                                card?.segment?.dest_arr_dt
                                    ? new Date(card.segment.dest_arr_dt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                    : '--:--'
                            }
                        </span>
                        <span className="result-card-date">
                            {card?.segment?.dest_arr_dt
                                ? new Date(card.segment.dest_arr_dt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
                                : ''}
                        </span>
                    </div>
                </div>

                {/* Footer Section */}
                <div className="result-card-footer">
                    <div className="result-card-left-footer">
                        {(() => {
                            const isTrain = card.type === 'train';
                            const seatUI = card?.segment?.seat_avail_ui;
                            const isInvalid = isTrain && (!seatUI || (seatUI.avl === null && seatUI.is_avail === false));

                            // Logic for Trains
                            if (isTrain) {
                                if (isInvalid || !effectiveSeatStatus) return null;
                            }

                            // Rendering
                            if (effectiveSeatStatus) {
                                return (
                                    <div className="seat-status-container" style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}>
                                        <div className="tooltip-container">
                                            <div className="tooltip-trigger">
                                                <Info size={18} color={
                                                    isBus
                                                        ? (effectiveSeatStatus && effectiveSeatStatus !== 'Sold out' ? "#16A34A" : "#DC2626")
                                                        : effectiveSeatStatus === "Seats available" ? "#16A34A"
                                                        : effectiveSeatStatus === "Seats partially available" ? "#CA8A04" : "#DC2626"
                                                } />
                                                <span className="tooltip-text">
                                                    {isBus
                                                        ? effectiveSeatStatus  // bus: show exact count e.g. "28 Seats Available"
                                                        : effectiveSeatStatus === "Seats available"
                                                            ? "Seats available on all segments of this route"
                                                            : effectiveSeatStatus === "Seats partially available"
                                                                ? "Seats are available on some segments of this route"
                                                                : "Seats are not available on this route"
                                                    }
                                                </span>
                                            </div>
                                        </div>
                                        <span className="seat-status-text" style={{
                                            fontSize: '15px',
                                            fontWeight: '600',
                                            color: isBus
                                                ? (effectiveSeatStatus && effectiveSeatStatus !== 'Sold out' ? "#16A34A" : "#DC2626")
                                                : effectiveSeatStatus === "Seats available" ? "#16A34A"
                                                : effectiveSeatStatus === "Seats partially available" ? "#CA8A04" : "#DC2626",
                                            textTransform: 'capitalize',
                                            fontFamily: "'Inter', sans-serif"
                                        }}>
                                            {effectiveSeatStatus}
                                        </span>
                                    </div>
                                );
                            } else if (!isTrain && card.fareOptions && card.fareOptions.length > 0) {
                                // Restore Swiper for Flights
                                return (
                                    <div className="fare-pills-swiper-container">
                                        <Swiper
                                            spaceBetween={8}
                                            slidesPerView="auto"
                                            freeMode={true}
                                            navigation={true}
                                            observer={true}
                                            observeParents={true}
                                            modules={[FreeMode, Navigation, Keyboard, Mousewheel]}
                                            keyboard={{ enabled: true }}
                                            mousewheel={{ forceToAxis: true }}
                                            className="fare-pills-swiper"
                                        >
                                            {card.fareOptions.map((option, idx) => (
                                                <SwiperSlide key={idx} style={{ width: 'auto' }}>
                                                    <div className="result-card-fare-pill">
                                                        <div className="result-card-fare-label-row">
                                                            <span className="result-card-class">{option.class}</span>
                                                            <span className="result-card-avail">{option.avail}</span>
                                                        </div>
                                                        <div className="result-card-tatkal-info">
                                                            <span className="result-card-quota-label">Total price</span>
                                                            <span className="result-card-fare-price">{option.price}</span>
                                                        </div>
                                                    </div>
                                                </SwiperSlide>
                                            ))}
                                        </Swiper>
                                    </div>
                                );
                            }
                            return null;
                        })()}

                    </div>

                    <div className="result-card-price-action">
                        <div className="result-card-actions">
                            {isFlight ? (
                                <button
                                    className={`result-card-book-btn ${mode}`}
                                    disabled={isBookingLoading}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleBookFlight();
                                    }}
                                >
                                    {isBookingLoading ? "Processing..." : `Book ${mode.charAt(0).toUpperCase() + mode.slice(1)}`}
                                </button>
                            ) : (
                                <>
                                <button
                                    className={`result-card-book-btn ${mode}`}
                                    disabled={isBookingLoading}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (isBus) {
                                            // Bus booking — navigate to seat selection page
                                            if (onBookClick) onBookClick();
                                            navigate('/book-bus', {
                                                state: {
                                                    busInfo:    card,
                                                    searchUrl:  location.search, // current search URL for Go Back
                                                    searchParams: {
                                                        from: card.segment?.origin_name,
                                                        to:   card.segment?.dest_name,
                                                        date: card.segment?.origin_dep_dt?.split('T')[0],
                                                    }
                                                }
                                            });
                                        } else if (card.segment?.booking_link) {
                                            window.open(card.segment.booking_link, '_blank', 'noopener,noreferrer');
                                        }
                                    }}
                                >
                                    {isBookingLoading ? (
                                        "Processing..."
                                    ) : (
                                        <>
                                            {mode === 'train'
                                                ? 'Book Train (IRCTC)'
                                                : isBus
                                                    ? 'Book Bus'
                                                    : `Book ${mode.charAt(0).toUpperCase() + mode.slice(1)}`
                                            }
                                            {mode === 'train' && <ExternalLink size={14} style={{ marginLeft: '6px' }} />}
                                        </>
                                    )}
                                </button>
                                </>
                            )}
                        </div>
                        <button
                            className="card-map-text-link"
                            onClick={(e) => { e.stopPropagation(); onViewMap(); }}
                        >
                            <FaRoute size={13} style={{ flexShrink: 0 }} />
                            View on Map
                        </button>
                        <div className="result-card-price-row">
                            <span className="result-card-final-price">
                                {displayPrice}
                            </span>
                            <button className="result-card-expand-btn">
                                {isExpanded ? <ChevronUp size={23} /> : <ChevronDown size={23} />}
                            </button>
                        </div>
                    </div>
                </div>

                {isFlight && card.flat_off_flight_promo_desc && (
                    <div className="flight-deal-banner-row">
                        <span className="flight-deal-badge">
                            <span className="flight-deal-dot"></span>
                            {card.flat_off_flight_promo_desc}
                        </span>
                    </div>
                )}
                {isBus && busPromoDesc && (
                    <div className="flight-deal-banner-row">
                        <span className="flight-deal-badge">
                            <span className="flight-deal-dot"></span>
                            {busPromoDesc}
                        </span>
                    </div>
                )}

                <LoadingModal
                    isOpen={isBookingLoading}
                    title="Processing Your Booking"
                    message="We are fetching the latest fare and availability for you. This will only take a moment."
                    showWarning={true}
                    warningText="Please do not refresh this page or use the back button while we process your request."
                />
            </div>

            {/* Expanded Content */}
            {isExpanded && (
                <div className="result-card-expanded-view" onClick={(e) => e.stopPropagation()}>
                    {(card.originCommentWeb || card.originCab) && (
                        <div className="timeline-segment">
                            <div className="sidebar-visual">
                                <div className="node-icon-box">
                                    <img src="/icon3.svg" alt="Commute" style={{ width: 24, height: 24 }} />
                                </div>
                                <div className="timeline-dashed-line"></div>
                            </div>
                            <div className="leg-details-container" style={{ flex: 1, paddingBottom: '24px' }}>
                                <div className="expanded-leg-card location-type commute-type">
                                    <div className="leg-upper">
                                        <span className="leg-name-title">{card.originCommentWebTitle || 'Commute'}</span>
                                        {card.originCab && (
                                            <span className="cab-selected-price">{formatPrice(selectedCabPrice(card.originCab, cabSel.origin))}</span>
                                        )}
                                        <span className="leg-type-tag">Commute</span>
                                    </div>
                                    <div className="leg-middle">
                                        {card.originCommentWeb && (
                                            <p className="location-desc-text" style={{ fontSize: '14px', color: '#475569', margin: 0 }}>{card.originCommentWeb}</p>
                                        )}
                                        <CabOptions
                                            cab={card.originCab}
                                            selected={cabSel.origin}
                                            onSelect={(c) => setCabSel(prev => ({ ...prev, origin: c }))}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {(() => {
                        const pseudoLeg = {
                            type: mode,
                            number: isBus ? '' : (card?.segment?.flight_num || card?.segment?.train_num || ''),
                            name: isBus
                                ? (card?.segment?.flight_name || card?.segment?.travel_name || '')
                                : (card?.segment?.flight_name || ''),
                            duration: card.duration,
                            price: card.price,
                            booking_link: card?.segment?.booking_link,
                            flat_off_flight_promo_desc: card?.segment?.flat_off_flight_promo_desc || null,
                            footerText: card?.segment?.seat_avail_ui?.explanation || card?.segment?.footerText,
                            origin: {
                                name: card?.segment?.origin_name || 'Origin',
                                code: card?.segment?.origin_code || 'ORG',
                                dep_dt: card?.segment?.origin_dep_dt || null,
                                time: card?.segment?.origin_dep_dt ? new Date(card.segment.origin_dep_dt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '--:--',
                                date: card?.segment?.origin_dep_dt ? new Date(card.segment.origin_dep_dt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : ''
                            },
                            destination: {
                                name: card?.segment?.dest_name || 'Destination',
                                code: card?.segment?.dest_code || 'DST',
                                time: card?.segment?.dest_arr_dt ? new Date(card.segment.dest_arr_dt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '--:--',
                                date: card?.segment?.dest_arr_dt ? new Date(card.segment.dest_arr_dt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : ''
                            },
                            seat_classes: card?.segment?.seat_classes,
                            seat_avail: card?.segment?.seat_avail,
                            seat_costs: card?.segment?.seat_costs,
                            category: card?.segment?.category,
                            train_class: card?.segment?.train_class,
                            seat_avail_ui: card?.segment?.seat_avail_ui,
                            seat_class_ui: card?.segment?.seat_class_ui
                        };

                        return (
                            <div className="timeline-segment">
                                <div className="sidebar-visual">
                                    <div className="node-icon-box">
                                        {pseudoLeg.type === 'flight' && <img src="/icon4.svg" alt="Flight" style={{ width: 24, height: 24 }} />}
                                        {pseudoLeg.type === 'train' && <img src="/icon1.svg" alt="Train" style={{ width: 24, height: 24 }} />}
                                        {pseudoLeg.type === 'bus' && <Bus size={20} color="#16A34A" />}
                                    </div>
                                    {card.destCommentWeb && <div className="timeline-dashed-line"></div>}
                                </div>
                                <div className="leg-details-container" style={{ flex: 1, paddingBottom: card.destCommentWeb ? '24px' : 0 }}>
                                    <div className={`expanded-leg-card ${pseudoLeg.type}-type`}>
                                        <div className="leg-upper">
                                            <span className="leg-name-title">
                                                {pseudoLeg.type === 'bus' ? (
                                                    <>
                                                        {pseudoLeg.name || ''}
                                                        {pseudoLeg.seat_class_ui && (
                                                            <span style={{ fontWeight: 400, color: '#94A3B8', fontSize: '13px', marginLeft: '6px' }}>
                                                                {pseudoLeg.seat_class_ui}
                                                            </span>
                                                        )}
                                                    </>
                                                ) : (
                                                    <>{pseudoLeg.number}{pseudoLeg.name ? `, ${pseudoLeg.name}` : ''}</>
                                                )}
                                            </span>
                                            <span className="leg-type-tag">
                                                {pseudoLeg.type.charAt(0).toUpperCase() + pseudoLeg.type.slice(1)}
                                            </span>
                                        </div>

                                        <div className="leg-middle">
                                            <div className="leg-stations">
                                                <div className="leg-station">
                                                    <span className="s-sub">{pseudoLeg.origin.name}</span>
                                                    <span className="s-main">{isBus ? firstWord(pseudoLeg.origin.name) : pseudoLeg.origin.code}, {pseudoLeg.origin.time}</span>
                                                    <span className="s-date">{pseudoLeg.origin.date}</span>
                                                </div>
                                                <div className="leg-visual">
                                                    <span className="d-text">{pseudoLeg.duration}</span>
                                                    <div className="d-line"></div>
                                                </div>
                                                <div className="leg-station right">
                                                    <span className="s-sub">{pseudoLeg.destination.name}</span>
                                                    <span className="s-main">{isBus ? firstWord(pseudoLeg.destination.name) : pseudoLeg.destination.code}, {pseudoLeg.destination.time}</span>
                                                    <span className="s-date">{pseudoLeg.destination.date}</span>
                                                </div>
                                            </div>
                                            {pseudoLeg.type === 'train' && (() => {
                                                const availableClasses = pseudoLeg.seat_classes?.filter(cls => cls !== 'GN') || [];
                                                if (availableClasses.length === 0) return null;

                                                // Ensure effective class always resolves
                                                const ui = pseudoLeg.seat_class_ui;
                                                const validUi = ui && availableClasses.some(c => compareClasses(c, ui)) ? ui : null;
                                                const defaultCls = validUi || availableClasses[0];
                                                const currentSelected = selectedSeatClass && availableClasses.some(c => compareClasses(c, selectedSeatClass))
                                                    ? selectedSeatClass
                                                    : defaultCls;

                                                // Compute activeData for the detail row
                                                const activeIdx = availableClasses.findIndex(c => compareClasses(c, currentSelected));
                                                const activeClsForData = activeIdx !== -1 ? currentSelected : availableClasses[0];
                                                const activeIdxForData = activeIdx !== -1 ? activeIdx : 0;
                                                const refreshedActive = refreshedInfo[activeIdxForData];
                                                const uiMatchActive = pseudoLeg.seat_avail_ui && compareClasses(pseudoLeg.seat_avail_ui.seat_class, activeClsForData);
                                                let activeAvailInfo = refreshedActive ? refreshedActive.avl : (uiMatchActive ? pseudoLeg.seat_avail_ui.avl : (pseudoLeg.seat_avail?.[activeClsForData] || null));
                                                let activeCost = refreshedActive ? refreshedActive.fare : (pseudoLeg.seat_costs?.[activeClsForData] || null);
                                                if (!activeCost || activeCost === '--') {
                                                    if (uiMatchActive) activeCost = pseudoLeg.seat_avail_ui.fare || null;
                                                    else if (pseudoLeg.seat_avail?.fare && (compareClasses(pseudoLeg.seat_class_ui, activeClsForData) || compareClasses(pseudoLeg.seat_avail.seat_class, activeClsForData))) {
                                                        activeCost = pseudoLeg.seat_avail.fare || null;
                                                    }
                                                }
                                                const activeAvailLower = (activeAvailInfo || '').toLowerCase();
                                                const activeIsAvbl = (activeAvailLower.includes('avail') || activeAvailLower.includes('avl') || activeAvailLower.includes('avbl')) && !activeAvailLower.includes('not');
                                                const activeStatusFromFlag = refreshedActive ? refreshedActive.is_avail : (uiMatchActive ? pseudoLeg.seat_avail_ui.is_avail : null);
                                                let activeStatusClass = 'unknown';
                                                if (activeStatusFromFlag === true) activeStatusClass = 'available';
                                                else if (activeStatusFromFlag === false) activeStatusClass = 'not-available';
                                                else if (activeAvailLower.includes('not') || activeAvailLower.includes('regret') || (activeAvailLower.includes('wl') && !activeIsAvbl)) activeStatusClass = 'not-available';
                                                else if (activeIsAvbl || activeAvailLower.includes('rac')) activeStatusClass = 'available';

                                                return (
                                                    <div className="seat-availability-swiper-container">
                                                        <Swiper
                                                            spaceBetween={12}
                                                            slidesPerView="auto"
                                                            freeMode={true}
                                                            navigation={true}
                                                            observer={true}
                                                            observeParents={true}
                                                            modules={[FreeMode, Navigation, Keyboard, Mousewheel]}
                                                            keyboard={{ enabled: true }}
                                                            mousewheel={{ forceToAxis: true }}
                                                            className="seat-availability-swiper"
                                                        >
                                                            {availableClasses.map((cls, idx) => {
                                                                const refreshed = refreshedInfo[idx];
                                                                const uiMatch = pseudoLeg.seat_avail_ui && compareClasses(pseudoLeg.seat_avail_ui.seat_class, cls);
                                                                let availInfo = refreshed ? refreshed.avl : (uiMatch ? pseudoLeg.seat_avail_ui.avl : (pseudoLeg.seat_avail?.[cls] || null));
                                                                let cost = refreshed ? refreshed.fare : (pseudoLeg.seat_costs?.[cls] || null);
                                                                if (!cost || cost === '--') {
                                                                    if (uiMatch) cost = pseudoLeg.seat_avail_ui.fare || null;
                                                                    else if (pseudoLeg.seat_avail?.fare && (compareClasses(pseudoLeg.seat_class_ui, cls) || compareClasses(pseudoLeg.seat_avail.seat_class, cls))) {
                                                                        cost = pseudoLeg.seat_avail.fare || null;
                                                                    }
                                                                }
                                                                let foundQuota = refreshed ? refreshed.quota : null;
                                                                if (!foundQuota) {
                                                                    if (pseudoLeg.seat_avail_ui && compareClasses(pseudoLeg.seat_avail_ui.seat_class, cls)) foundQuota = pseudoLeg.seat_avail_ui.quota;
                                                                    else if (pseudoLeg.seat_avail && (compareClasses(pseudoLeg.seat_class_ui, cls) || (pseudoLeg.seat_avail.seat_class && compareClasses(pseudoLeg.seat_avail.seat_class, cls)))) foundQuota = pseudoLeg.seat_avail.quota;
                                                                }
                                                                let displayCls = cls;
                                                                let hasData = (cost && cost !== '--') || (availInfo && availInfo !== 'N/A' && availInfo !== null);
                                                                let displayQuota = hasData ? (foundQuota || pseudoLeg.category || 'GN') : 'N/A';
                                                                const quotaCodes = ['GN', 'TQ', 'PT', 'SS', 'LD', 'DF', 'FT'];
                                                                if ((displayQuota.toLowerCase().includes('reserved') || displayQuota === 'Reserved') && quotaCodes.includes(displayCls.toUpperCase())) {
                                                                    displayQuota = displayCls;
                                                                    displayCls = pseudoLeg.train_class || 'N/A';
                                                                }
                                                                if (displayQuota.toLowerCase().includes('reserved')) displayQuota = 'GN';
                                                                const availLower = (availInfo || '').toLowerCase();
                                                                const isAvbl = (availLower.includes('avail') || availLower.includes('avl') || availLower.includes('avbl')) && !availLower.includes('not');
                                                                const isRac = availLower.includes('rac');
                                                                const isReserved = availLower.includes('reserved') || availLower.includes('resv');
                                                                const isNotAvail = availLower.includes('not') || availLower.includes('regret') || (availLower.includes('wl') && !isAvbl);
                                                                const statusFromFlag = refreshed ? refreshed.is_avail : (pseudoLeg.seat_avail_ui && compareClasses(pseudoLeg.seat_avail_ui.seat_class, cls) ? pseudoLeg.seat_avail_ui.is_avail : null);
                                                                let statusClass = 'unknown';
                                                                if (statusFromFlag === true) statusClass = 'available';
                                                                else if (statusFromFlag === false) statusClass = 'not-available';
                                                                else if (isNotAvail) statusClass = 'not-available';
                                                                else if (isRac || isAvbl) statusClass = 'available';
                                                                else if (isReserved) statusClass = 'reserved';
                                                                const isSelected = compareClasses(currentSelected, cls);
                                                                return (
                                                                    <SwiperSlide key={idx} style={{ width: 'auto' }}>
                                                                        <div
                                                                            className={`seat-class-box ${statusClass} ${isSelected ? 'selected-seat-class' : ''}`}
                                                                            onClick={(e) => { e.stopPropagation(); setSelectedSeatClass(cls); }}
                                                                        >
                                                                            <div className="seat-card-grid">
                                                                                <div className="seat-grid-top-left">{displayCls}</div>
                                                                                <div className="seat-grid-top-right">({displayQuota})</div>
                                                                                <div className="seat-grid-bottom-left">
                                                                                    <span className={`seat-status-text-styled ${statusClass}`}>{availInfo || 'N/A'}</span>
                                                                                </div>
                                                                                <div className="seat-grid-bottom-right">{cost ? `₹${cost}` : '--'}</div>
                                                                            </div>
                                                                            <div
                                                                                className={`seat-refresh-layer ${refreshingIdx === idx ? 'refreshing' : ''}`}
                                                                                onClick={(e) => { e.stopPropagation(); setSelectedSeatClass(cls); handleRefresh(pseudoLeg, cls, idx); }}
                                                                            >
                                                                                {refreshingIdx === idx ? (
                                                                                    <svg width="22" height="22" viewBox="0 0 50 50" className="seat-loading-spinner">
                                                                                        <circle cx="25" cy="25" r="20" stroke="#888" strokeWidth="4" fill="none" strokeDasharray="31.4 31.4" strokeLinecap="round">
                                                                                            <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="1s" repeatCount="indefinite" />
                                                                                        </circle>
                                                                                    </svg>
                                                                                ) : (
                                                                                    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" className="seat-refresh-svg">
                                                                                        <path d="M4 4v5h5" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                                                        <path d="M2.5 10a7.5 7.5 0 1 1 2.2 5.3" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                                                    </svg>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </SwiperSlide>
                                                                );
                                                            })}
                                                        </Swiper>
                                                        <div className="seat-availability-detail-text">
                                                            {refreshingIdx === activeIdxForData ? (
                                                                <div className="seat-detail-skeleton">
                                                                    <div className="skeleton-bar" style={{ width: 90 }} />
                                                                    <div className="skeleton-bar" style={{ width: 50 }} />
                                                                </div>
                                                            ) : (
                                                                <div></div>
                                                            )}
                                                            <div className="seat-detail-right">
                                                                <button
                                                                    className="book-leg-btn train"
                                                                    onClick={(e) => { e.stopPropagation(); if (pseudoLeg.booking_link) window.open(pseudoLeg.booking_link, '_blank', 'noopener,noreferrer'); }}
                                                                >
                                                                    Book on IRCTC
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                        {pseudoLeg.type !== 'train' && (
                                            <div className="leg-lower">
                                                <div className="leg-price-container">
                                                    {pseudoLeg.type === 'flight' && displayPrice && displayPrice !== '-' && (
                                                        <span className="leg-price-val">{displayPrice}</span>
                                                    )}
                                                </div>
                                                {pseudoLeg.type === 'flight' ? (
                                                    <div className="leg-button-wrapper">
                                                        <button
                                                            className={`book-leg-btn ${pseudoLeg.type}`}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleBookFlight();
                                                            }}
                                                        >
                                                            Book Flight
                                                        </button>
                                                        {card.flat_off_flight_promo_desc && (
                                                            <span className="flight-deal-badge">
                                                                <span className="flight-deal-dot"></span>
                                                                {card.flat_off_flight_promo_desc}
                                                            </span>
                                                        )}
                                                    </div>
                                                ) : pseudoLeg.type === 'bus' ? (
                                                    <div className="leg-button-wrapper">
                                                        <button
                                                            className="book-leg-btn bus"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (onBookClick) onBookClick();
                                                                navigate('/book-bus', {
                                                                    state: {
                                                                        busInfo: card,
                                                                        searchUrl: location.search,
                                                                        searchParams: {
                                                                            from: card.segment?.origin_name,
                                                                            to:   card.segment?.dest_name,
                                                                            date: card.segment?.origin_dep_dt?.split('T')[0],
                                                                        }
                                                                    }
                                                                });
                                                            }}
                                                        >
                                                            Book Bus
                                                        </button>
                                                        {pseudoLeg.flat_off_flight_promo_desc && (
                                                            <span className="flight-deal-badge">
                                                                <span className="flight-deal-dot"></span>
                                                                {pseudoLeg.flat_off_flight_promo_desc}
                                                            </span>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div className="leg-button-wrapper">
                                                        <button
                                                            className={`book-leg-btn ${pseudoLeg.type}`}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (pseudoLeg.booking_link) {
                                                                    window.open(pseudoLeg.booking_link, '_blank', 'noopener,noreferrer');
                                                                }
                                                            }}
                                                        >
                                                            {pseudoLeg.type === 'train' ? 'Book on IRCTC' : `Book ${pseudoLeg.type.charAt(0).toUpperCase() + pseudoLeg.type.slice(1)}`}
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {(pseudoLeg.footerText || card.explanation) && (
                                            <div className="leg-footer-msg">
                                                {[
                                                    ...(card.explanation ? [card.explanation] : []),
                                                    ...(pseudoLeg.footerText ? pseudoLeg.footerText.split('\n') : [])
                                                ].filter(line => line.trim() !== '').map((line, i) => <p key={i}>{line}</p>)}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })()}

                    {(card.destCommentWeb || card.destCab) && (
                        <div className="timeline-segment" style={{ marginTop: '0px' }}>
                            <div className="sidebar-visual">
                                <div className="node-icon-box">
                                    <img src="/icon3.svg" alt="Commute" style={{ width: 24, height: 24 }} />
                                </div>
                            </div>
                            <div className="leg-details-container" style={{ flex: 1 }}>
                                <div className="expanded-leg-card location-type commute-type">
                                    <div className="leg-upper">
                                        <span className="leg-name-title">{card.destCommentWebTitle || 'Reach Destination'}</span>
                                        {card.destCab && (
                                            <span className="cab-selected-price">{formatPrice(selectedCabPrice(card.destCab, cabSel.dest))}</span>
                                        )}
                                        <span className="leg-type-tag">Commute</span>
                                    </div>
                                    <div className="leg-middle">
                                        {card.destCommentWeb && (
                                            <p className="location-desc-text" style={{ fontSize: '14px', color: '#475569', margin: 0 }}>{card.destCommentWeb}</p>
                                        )}
                                        <CabOptions
                                            cab={card.destCab}
                                            selected={cabSel.dest}
                                            onSelect={(c) => setCabSel(prev => ({ ...prev, dest: c }))}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
            <LoadingModal
                isOpen={isBookingLoading}
                title="Processing Your Booking"
                message="We are fetching the latest fare and availability for you. This will only take a moment."
                showWarning={true}
                warningText="Please do not refresh this page or use the back button while we process your request."
            />
        </div>
    );
};

export default ResultCard;
